/**
 * Field-state highlighting, for every HelpDesk form (product owner, 5 Oct).
 *
 * WHY: forms open more and more prefilled (the wizard, a chat card, a New
 * starter step) and people could not tell what the app had filled in from
 * what they still had to answer. So a field the app filled — and the person
 * has not changed — is tinted, and a required field still empty is tinted
 * differently. Editing a prefilled field returns it to normal; answering a
 * required one removes its tint.
 *
 * Colours (7 Oct, product owner's UAT walkthrough): Norican's own, and
 * subtler than the first green and purple. Filled in for you is a light wash
 * of Accent Blue; still needed is a light wash of Norican Yellow.
 *
 * Pure (no React): the field renderers call `useFieldHighlight` and wrap the
 * field in `<HighlightedField>`; node tests import this file directly.
 */

/** How a field is tinted: filled in by the app, still needed, or neither. */
export type FieldHighlight = "prefilled" | "required" | null;

/** A field's value in any of the shapes the forms keep. */
export type FieldValue = string | readonly string[] | boolean | null | undefined;

/** Nothing in it: null, blank text, an empty list, or `false`/"false" (an unticked box). */
export function isEmptyValue(value: FieldValue): boolean {
  if (value === null || value === undefined || value === false) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim().length === 0 || value === "false";
  }
  if (typeof value === "boolean") {
    return false;
  }
  return value.every((part) => part.trim().length === 0);
}

/** The same answer, ignoring surrounding whitespace and the order of a multi-select. */
export function sameValue(left: FieldValue, right: FieldValue): boolean {
  const norm = (value: FieldValue): string => {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string" || typeof value === "boolean") {
      return String(value).trim();
    }
    return [...value]
      .map((part) => part.trim())
      .sort()
      .join("\u0000");
  };
  return norm(left) === norm(right);
}

/**
 * The highlight for a field.
 *
 * @param prefilledValue What the app put in the field (null/undefined: nothing).
 * @param currentValue What is in it now.
 * @param required Whether the field must be answered.
 */
export function fieldHighlight(
  prefilledValue: FieldValue,
  currentValue: FieldValue,
  required: boolean,
): FieldHighlight {
  const empty = isEmptyValue(currentValue);
  if (!empty && !isEmptyValue(prefilledValue) && sameValue(prefilledValue, currentValue)) {
    return "prefilled";
  }
  if (required && empty) {
    return "required";
  }
  return null;
}

/**
 * The highlight for an optional field the app may have filled in: blue while
 * it still holds the app's value, plain once edited or emptied — never yellow.
 * The incident form's follow-up answers ("Here's what you told us"), its
 * impact choice and its "For: <name>" line use it (7 Oct).
 */
export function prefillHighlight(
  prefilledValue: FieldValue,
  currentValue: FieldValue,
): FieldHighlight {
  return fieldHighlight(prefilledValue, currentValue, false);
}

/**
 * `fieldHighlight` under the hook name the field renderers use. It holds no
 * state — the "edited" test is the value no longer equalling the prefilled
 * one — so it may be called anywhere a hook may.
 */
export function useFieldHighlight(
  prefilledValue: FieldValue,
  currentValue: FieldValue,
  required: boolean,
): FieldHighlight {
  return fieldHighlight(prefilledValue, currentValue, required);
}

/**
 * The wrapper classes per state, from Norican tokens only. Every state keeps
 * the same padding and a start border (transparent when plain), so a field
 * does not move when its tint comes or goes.
 *
 * Both washes are mixed into transparent, so they sit on whatever surface the
 * form is on, in either theme, and the text on them keeps its full contrast
 * (Norican Blue on a ~6% tint of white stays above 15:1). The start border
 * carries the colour; the tint only supports it, and is never the only cue:
 * required fields keep their asterisk, prefilled ones their "filled in for
 * you" notes.
 *
 * - prefilled: Accent Blue (`--norican-accent`, #115cd3), border at 45%,
 *   wash at 6%.
 * - required: Norican Yellow (`--norican-color-brand-yellow-500`, #f7c037),
 *   border full, wash at 12%.
 */
export const FIELD_HIGHLIGHT_CLASSES: Record<"base" | "prefilled" | "required" | "none", string> = {
  base: "rounded-md border-s-4 py-2 ps-3 pe-2 transition-colors",
  prefilled:
    "border-[color-mix(in_oklab,var(--norican-accent)_45%,transparent)] bg-[color-mix(in_oklab,var(--norican-accent)_6%,transparent)]",
  required:
    "border-[var(--norican-color-brand-yellow-500)] bg-[color-mix(in_oklab,var(--norican-color-brand-yellow-500)_12%,transparent)]",
  none: "border-transparent",
};

/** The class list for a state. */
export function fieldHighlightClass(state: FieldHighlight): string {
  return `${FIELD_HIGHLIGHT_CLASSES.base} ${FIELD_HIGHLIGHT_CLASSES[state ?? "none"]}`;
}
